// @ts-nocheck
/**
 * Admin Navigation Component
 * Carbon UI Shell SideNav implementation for admin dashboard
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminNavigation.module.scss';
import { SideNav, SideNavItems, SideNavLink, SideNavMenuItem, SideNavMenu } from '@carbon/react';
import {
  Dashboard,
  UserMultiple,
  Analytics,
  Edit,
  Settings,
  User,
  Security,
  Api,
  DatabaseMessaging,
  TestTool,
  ChartLine,
  View,
  ContentDeliveryNetwork,
  Education,
  Promote,
  CloudApp,
  Screen, // Changed from Monitor - not available in Carbon icons
  CloudBackup,
  DataCheck,
} from '@carbon/icons-react';

interface AdminNavigationProps {
  isOpen: boolean;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const AdminNavigation: React.FC<AdminNavigationProps> = ({ isOpen, activeTab, onTabChange }) => {
  const navigate = useNavigate();

  const handleNavItemClick = (tabId: string) => {
    // Map tab IDs to their URL routes
    const routeMap: { [key: string]: string } = {
      backup: '/admin/backup',
      'platform-monitoring': '/admin/platform-monitoring',
      'data-management': '/admin/data-management',
      'data-routing': '/admin/data-routing',
      'consistency-monitoring': '/admin/consistency',
      users: '/admin/users',
      marketing: '/admin/marketing',
      analytics: '/admin/marketing/analytics',
      'ab-testing': '/admin/ab-testing',
      'cache-management': '/admin/cache-management',
      'rbac-permissions': '/admin/rbac-dashboard',
      'security-settings': '/admin/rbac/compliance',
      'audit-logs': '/admin/rbac/audit',
      overview: '/admin',
    };

    // If tab has a dedicated route, navigate to it
    if (routeMap[tabId]) {
      navigate(routeMap[tabId]);
    } else {
      // Otherwise, just update tab state (for tabs handled within AdminDashboard)
      onTabChange(tabId);
    }
  };

  return (
    <SideNav isFixedNav expanded={isOpen} aria-label="Admin navigation" className={styles.adminNavigation}>
      <SideNavItems>
        {/* Overview Section */}
        <SideNavLink
          renderIcon={Dashboard}
          onClick={() => handleNavItemClick('overview')}
          isActive={activeTab === 'overview'}
          className={`${styles.navItem} ${activeTab === 'overview' ? styles['navItem--active'] : ''}`}
        >
          Dashboard Overview
        </SideNavLink>

        {/* Platform Monitoring */}
        <SideNavLink
          renderIcon={Screen}
          onClick={() => handleNavItemClick('platform-monitoring')}
          isActive={activeTab === 'platform-monitoring'}
          className={`${styles.navItem} ${activeTab === 'platform-monitoring' ? styles['navItem--active'] : ''}`}
        >
          Platform Monitoring
        </SideNavLink>

        {/* User Management */}
        <SideNavMenu
          renderIcon={UserMultiple}
          title="User Management"
          defaultExpanded={activeTab.startsWith('user') || activeTab === 'multi-tenant'}
        >
          <SideNavMenuItem
            onClick={() => handleNavItemClick('users')}
            isActive={activeTab === 'users'}
            style={{
              color: activeTab === 'users' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor: activeTab === 'users' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            User Accounts
          </SideNavMenuItem>
          <SideNavMenuItem
            onClick={() => handleNavItemClick('multi-tenant')}
            isActive={activeTab === 'multi-tenant'}
            style={{
              color: activeTab === 'multi-tenant' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor: activeTab === 'multi-tenant' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            Multi-Tenant Management
          </SideNavMenuItem>
        </SideNavMenu>

        {/* Analytics & Reporting */}
        <SideNavMenu renderIcon={Analytics} title="Analytics & Reporting" defaultExpanded={activeTab === 'analytics'}>
          <SideNavMenuItem
            onClick={() => handleNavItemClick('analytics')}
            isActive={activeTab === 'analytics'}
            style={{
              color: activeTab === 'analytics' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor: activeTab === 'analytics' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            Institutional Analytics
          </SideNavMenuItem>
        </SideNavMenu>

        {/* Marketing Management - Standalone with Promote Icon */}
        <SideNavLink
          renderIcon={Promote}
          onClick={() => handleNavItemClick('marketing')}
          isActive={activeTab === 'marketing'}
          style={{
            color: activeTab === 'marketing' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
            backgroundColor: activeTab === 'marketing' ? 'var(--cds-background-selected)' : 'transparent',
          }}
        >
          Marketing Management
        </SideNavLink>

        {/* Content Management */}
        <SideNavMenu renderIcon={Edit} title="Content Management" defaultExpanded={false}>
          <SideNavMenuItem onClick={() => handleNavItemClick('content-editor')}>Content Editor</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('content-library')}>Content Library</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('approval-workflow')}>Approval Workflow</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('publishing-scheduler')}>
            Publishing Scheduler
          </SideNavMenuItem>
        </SideNavMenu>

        {/* System Management */}
        <SideNavMenu
          renderIcon={Settings}
          title="System Management"
          defaultExpanded={activeTab === 'system' || activeTab === 'backup'}
        >
          <SideNavMenuItem
            onClick={() => handleNavItemClick('system')}
            isActive={activeTab === 'system'}
            style={{
              color: activeTab === 'system' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor: activeTab === 'system' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            System Configuration
          </SideNavMenuItem>
          <SideNavMenuItem
            onClick={() => handleNavItemClick('backup')}
            isActive={activeTab === 'backup'}
            style={{
              color: activeTab === 'backup' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor: activeTab === 'backup' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            Backup & Recovery
          </SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('database-management')}>
            Database Management
          </SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('api-configuration')}>API Configuration</SideNavMenuItem>
        </SideNavMenu>

        {/* Security & Permissions */}
        <SideNavMenu renderIcon={Security} title="Security & Permissions" defaultExpanded={false}>
          <SideNavMenuItem onClick={() => handleNavItemClick('rbac-permissions')}>RBAC Permissions</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('security-settings')}>Security Settings</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('audit-logs')}>Audit Logs</SideNavMenuItem>
        </SideNavMenu>

        {/* A/B Testing & Optimization */}
        <SideNavMenu renderIcon={TestTool} title="A/B Testing & Optimization" defaultExpanded={false}>
          <SideNavMenuItem onClick={() => handleNavItemClick('ab-testing')}>A/B Test Configuration</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('winner-selection')}>
            Winner Selection Panel
          </SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('personalization')}>
            Personalization Editor
          </SideNavMenuItem>
        </SideNavMenu>

        {/* Performance & Cache */}
        <SideNavMenu
          renderIcon={ContentDeliveryNetwork}
          title="Performance & Cache"
          defaultExpanded={
            activeTab === 'cache-management' ||
            activeTab === 'data-routing' ||
            activeTab === 'consistency-monitoring' ||
            activeTab === 'version-history' ||
            activeTab === 'performance-metrics'
          }
        >
          <SideNavMenuItem onClick={() => handleNavItemClick('cache-management')}>Cache Management</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('data-routing')}>Data Routing</SideNavMenuItem>
          <SideNavMenuItem
            onClick={() => handleNavItemClick('consistency-monitoring')}
            isActive={activeTab === 'consistency-monitoring'}
            style={{
              color: activeTab === 'consistency-monitoring' ? 'var(--cds-interactive-01)' : 'var(--cds-text-primary)',
              backgroundColor:
                activeTab === 'consistency-monitoring' ? 'var(--cds-background-selected)' : 'transparent',
            }}
          >
            Consistency Monitoring
          </SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('version-history')}>Version History</SideNavMenuItem>
          <SideNavMenuItem onClick={() => handleNavItemClick('performance-metrics')}>
            Performance Metrics
          </SideNavMenuItem>
        </SideNavMenu>
      </SideNavItems>
    </SideNav>
  );
};

export default AdminNavigation;
